// src/components/landing/HowItWorksSection.jsx
import React, { useState, useEffect } from 'react';
import SystemMap from './SystemMap';
import '../../styles/howItWorks.css';

/** Image assets in /public/landing_page_hero/how_it_works_section */
const IMG_BASE = '/landing_page_hero/how_it_works_section';

/** Campus buildings configuration - EXACTLY 4 buildings, no duplicates */
const CAMPUS_BUILDINGS = [
  {
    id: 'notes',
    label: 'Notes Library',
    img: `${IMG_BASE}/study_tools_library.png`,
    description: 'Where your uploads become clean, AI-organized notes and summaries.',
    position: { left: '82%', top: '33%' }, // bottom-right
  },
  {
    id: 'gamification',
    label: 'Gamification Arena',
    img: `${IMG_BASE}/gamification_arena.png`,
    description: 'Trophies, streaks, and challenges that reward real study time.',
    position: { left: '61%', top: '24%' }, // mid-right
  },
  {
    id: 'productivity',
    label: 'Productivity Hall',
    img: `${IMG_BASE}/productivity_hall.png`,
    description: 'A calm control center for deadlines, tasks, and schedules.',
    position: { left: '33%', top: '23%' }, // upper-middle, NOT on top of statue
  },
  {
    id: 'companion',
    label: 'Companion Pavilion',
    img: `${IMG_BASE}/companion_pavilion.png`,
    description: "Scout's home base for chat, reminders, and quick tools.",
    position: { left: '20%', top: '43%' }, // lower-left
  },
];

/** Default hint card when no building is selected */
const DefaultScoutHint = () => (
  <div className="howItWorks__defaultHintCard">
    <img
      src="/scout_images/Scout_Thumbsup.png"
      alt="Scout"
      className="howItWorks__defaultHintScout"
    />
    <div className="howItWorks__defaultHintText">
      Hover over a building on the campus map to learn what it does.
    </div>
  </div>
);

/** CampusCard component for Step 3 - Expanded campus world */
// eslint-disable-next-line no-unused-vars
const CampusCard = () => {
  const [hoveredBuildingId, setHoveredBuildingId] = useState(null);
  
  const displayedBuilding = hoveredBuildingId ? CAMPUS_BUILDINGS.find(b => b.id === hoveredBuildingId) : null;

  return (
    <div className="howItWorks__campusCard">
      {/* Map overlay with building markers */}
      <div className="howItWorks__campusMapOverlay" aria-hidden="true">
        {CAMPUS_BUILDINGS.map((building) => (
          <button
            key={building.id}
            type="button"
            className={`howItWorks__buildingMarker ${
              building.id === hoveredBuildingId ? 'howItWorks__buildingMarker--active' : ''
            }`}
            style={{
              left: building.position.left,
              top: building.position.top,
              outline: 'none',
              border: 'none',
            }}
            onMouseEnter={() => setHoveredBuildingId(building.id)}
            onMouseLeave={() => setHoveredBuildingId(null)}
            onFocus={() => setHoveredBuildingId(building.id)}
            onBlur={() => setHoveredBuildingId(null)}
            aria-label={`View ${building.label}`}
          >
            <img
              src={`${IMG_BASE}/building_highlight_burst.png`}
              alt=""
              aria-hidden="true"
              className="howItWorks__buildingHighlight"
            />
            <img
              src={building.img}
              alt={building.label}
              className={`howItWorks__buildingImage ${
                building.id === 'notes' ? 'howItWorks__buildingImage--flipped' : ''
              } ${
                building.id === 'gamification' ? 'howItWorks__buildingImage--large howItWorks__buildingImage--flipped' : ''
              } ${
                building.id === 'productivity' ? 'howItWorks__buildingImage--xlarge' : ''
              } ${
                building.id === 'companion' ? 'howItWorks__buildingImage--double' : ''
              }`}
            />
          </button>
        ))}
      </div>

      {/* STEP COPY CARD – bottom-right */}
      <section className="howItWorks__campusTextCard">
        <h3>3. Explore your learning campus</h3>
        <ul>
          <li>Notes Library, Gamification Arena, Productivity Hall, Companion Pavilion</li>
          <li>Everything stays in one organized learning campus</li>
          <li>Scout is always there if you want to chat or generate a new tool</li>
        </ul>
      </section>

      {/* BUILDING INFO CARD – bottom-left */}
      {displayedBuilding ? (
        <aside className="howItWorks__campusInfoCard">
          <img 
            src={displayedBuilding.img} 
            alt={displayedBuilding.label}
            className="howItWorks__campusInfoCardImage"
          />
          <div className="howItWorks__campusInfoCardText">
            <h4>{displayedBuilding.label}</h4>
            <p>{displayedBuilding.description}</p>
          </div>
        </aside>
      ) : (
        <DefaultScoutHint />
      )}
    </div>
  );
};

const HowItWorksSection = () => {
  const [activeStep, setActiveStep] = useState(1);
  const [, setTiltStyle] = useState({ transform: 'translate(0, 0)' });
  const [showScoutInMagic, setShowScoutInMagic] = useState(true);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [showPdfInMagic, setShowPdfInMagic] = useState(false);

  // Handle Scout appearance in Step 2: show for 3s then fade out
  useEffect(() => {
    if (activeStep === 2) {
      // Reset to visible whenever we enter step 2
      setShowScoutInMagic(true);
      setIsFadingOut(false);
      setShowPdfInMagic(false);

      // Start fade after 2.6s, unmount after 3s
      const fadeTimer = setTimeout(() => {
        setIsFadingOut(true);
      }, 2600);

      const hideTimer = setTimeout(() => {
        setShowScoutInMagic(false);
        setShowPdfInMagic(true);
      }, 3000);

      return () => {
        clearTimeout(fadeTimer);
        clearTimeout(hideTimer);
      };
    } else {
      // Reset when leaving step 2
      setShowScoutInMagic(true);
      setIsFadingOut(false);
      setShowPdfInMagic(false);
    }
  }, [activeStep]);

  /** Step configuration with all copy and asset references */
  const steps = [
    {
      id: 1,
      key: 'upload',
      shortLabel: 'Upload',
      caption: 'Drop your syllabus',
      title: '1. Upload your syllabus',
      bullets: [
        'Supports PDFs and most text-based formats',
        'Handles multiple classes in one go',
        'No manual data entry or spreadsheet work',
      ],
      assets: {
        type: 'scoutUpload',
        image: `${IMG_BASE}/scout_holding_document.png`,
      },
    },
    {
      id: 2,
      key: 'magic',
      shortLabel: 'Magic builds',
      caption: 'Scout builds your workspace',
      title: '2. DocuHounds builds your semester',
      bullets: [
        'Auto-generated calendar with synced due dates',
        'Timeline view for exams, projects, and readings',
        "Clickable cards that jump into Scout's AI tools",
      ],
      assets: {
        type: 'scoutOrbit',
        scout: `${IMG_BASE}/scout_tossing_document.png`,
        doc: `${IMG_BASE}/pdf_icon.png`,
        rings: `${IMG_BASE}/magic_ripple.png`,
      },
    },
    {
      id: 3,
      key: 'explore',
      shortLabel: 'Explore workspace',
      caption: 'Navigate your system',
      title: '3. Explore your workspace',
      bullets: [
        'Everything your syllabus creates lives in one connected workspace',
        'Jump from course → timeline → tools without hunting',
        'Your progress stays organized across every class',
      ],
      assets: {
        type: 'system',
      },
    },
  ];

  const isActive = (stepId) => activeStep === stepId;

  const handleNext = () => {
    setActiveStep((prev) => (prev === steps.length ? 1 : prev + 1));
  };

  const handlePrev = () => {
    setActiveStep((prev) => (prev === 1 ? steps.length : prev - 1));
  };

  // Subtle parallax effect for Step 3 campus map
  // eslint-disable-next-line no-unused-vars
  const handleMouseMove = (e) => {
    if (activeStep !== 3) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left - rect.width / 2) / rect.width;
    const y = (e.clientY - rect.top - rect.height / 2) / rect.height;
    setTiltStyle({
      transform: `translate(${x * -6}px, ${y * -6}px)`,
    });
  };

  // eslint-disable-next-line no-unused-vars
  const handleMouseLeave = () => {
    setTiltStyle({ transform: 'translate(0, 0)' });
  };

  return (
    <section id="how-it-works" className="howItWorks">
      {/* Header Strip */}
      <header className="howItWorks__header">
        <h2>How it works</h2>
        <p>
          One syllabus upload turns into a living campus: dashboard, timeline,
          and AI study tools you can actually use.
        </p>
      </header>

      {/* Horizontal Step Bar */}
      <ol className="howItWorks__steps">
        {steps.map((step, index) => (
          <li
            key={step.id}
            className={`howItWorks__step ${
              isActive(step.id) ? 'howItWorks__step--active' : ''
            }`}
          >
            <button
              type="button"
              onClick={() => setActiveStep(step.id)}
              aria-pressed={isActive(step.id)}
            >
              <span className="howItWorks__stepIndex">{index + 1}</span>
              <span className="howItWorks__stepLabel">{step.shortLabel}</span>
            </button>
            <p className="howItWorks__stepCaption">{step.caption}</p>
          </li>
        ))}
      </ol>

      {/* Card Row with Prev/Next */}
      <div className="howItWorks__cardRow">
        <button
          type="button"
          className="howItWorks__navBtn howItWorks__navBtn--prev"
          onClick={handlePrev}
          aria-label="Previous step"
        >
          ‹
        </button>

        <article 
          className="howItWorks__card"
          aria-live="polite"
        >
          {activeStep === 1 && (
            <div className="howItWorks__cardContent is-active">
              {/* Text content */}
              <div className="howItWorks__stepText">
                <h3>{steps[0].title}</h3>
                <ul className="howItWorks__bullets">
                  {steps[0].bullets.map((bullet, idx) => (
                    <li key={idx}>{bullet}</li>
                  ))}
                </ul>
              </div>

              {/* Visual content - Step 1: Scout Upload */}
              <div className="howItWorks__stepVisual">
                <div className="howItWorks__uploadScene">
                  <div className="howItWorks__sparkles" aria-hidden="true" />
                  <img
                    src={steps[0].assets.image}
                    alt="Scout the Beagle holding a syllabus document"
                    className="howItWorks__scout howItWorks__scout--upload"
                  />
                </div>
              </div>
            </div>
          )}

          {activeStep === 2 && (
            <div className="howItWorks__cardContent is-active">
              {/* Text content */}
              <div className="howItWorks__stepText">
                <h3>{steps[1].title}</h3>
                <ul className="howItWorks__bullets">
                  {steps[1].bullets.map((bullet, idx) => (
                    <li key={idx}>{bullet}</li>
                  ))}
                </ul>
              </div>

              {/* Visual content - Step 2: Orbit Magic */}
              <div className="howItWorks__stepVisual">
                <div className="howItWorks__magicScene">
                  <div className="howItWorks__orbitGlow" aria-hidden="true" />
                  <img
                    src={steps[1].assets.rings}
                    alt=""
                    className="howItWorks__magicRipple"
                    aria-hidden="true"
                  />
                  <img
                    src={steps[1].assets.doc}
                    alt="Your syllabus data flowing through DocuHounds"
                    className={`howItWorks__magicDocIcon ${
                      showPdfInMagic ? 'is-visible' : ''
                    }`}
                  />
                  {showScoutInMagic && (
                    <img
                      src={steps[1].assets.scout}
                      alt="Scout tossing your syllabus into the system"
                      className={`howItWorks__scout howItWorks__scout--magic ${
                        isFadingOut ? 'is-fading' : ''
                      }`}
                    />
                  )}
                </div>
              </div>
            </div>
          )}

          {activeStep === 3 && (
            <div className="howItWorks__cardContent is-active">
              {/* Text content */}
              <div className="howItWorks__stepText">
                <h3>{steps[2].title}</h3>
                <ul className="howItWorks__bullets">
                  {steps[2].bullets.map((bullet, idx) => (
                    <li key={idx}>{bullet}</li>
                  ))}
                </ul>
              </div>

              {/* Visual content - Step 3: System Map */}
              <div className="howItWorks__stepVisual howItWorks__stepVisual--system">
                <SystemMap />
              </div>
            </div>
          )}
        </article>

        <button
          type="button"
          className="howItWorks__navBtn howItWorks__navBtn--next"
          onClick={handleNext}
          aria-label="Next step"
        >
          ›
        </button>
      </div>
    </section>
  );
};

export default HowItWorksSection;
